'use client';

import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { SignInButton, useUser } from '@clerk/nextjs';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/cn';

const freeFeatures = [
  'Up to 5 assets',
  'Cloud sync',
  'Stock news',
  'Portfolio Heatmap',
  'Themes'
];

const proFeatures = [
  'Unlimited assets',
  'Equity Research',
  'ETF allocations',
  'Early access to features',
];

function FeatureRow({ label }: { label: string }) {
  return (
    <li className="flex items-center gap-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
      <Check size={16} className="text-green-500 flex-shrink-0" />
      {label}
    </li>
  );
}

export function PricingSection() {
  const { isSignedIn } = useUser();
  const router = useRouter();

  return (
    <section className="py-20">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-[var(--color-text)]">
            Simple Pricing
          </h2>
          <p className="mt-3 text-lg" style={{ color: 'var(--color-text-secondary)' }}>
            Start free, upgrade when you&apos;re ready.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Free Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className={cn(
              "rounded-2xl p-6 border border-[var(--color-border-subtle)]"
            )}
            style={{ backgroundColor: 'var(--color-surface)' }}
          >
            <h3 className="text-lg font-semibold text-[var(--color-text)]">Free</h3>
            <div className="mt-4 mb-6">
              <span className="text-4xl font-bold text-[var(--color-text)]">$0</span>
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}> / forever</span>
            </div>
            <ul className="space-y-3 mb-8">
              {freeFeatures.map((f) => (
                <FeatureRow key={f} label={f} />
              ))}
            </ul>
            {isSignedIn ? (
              <button
                onClick={() => router.push('/')}
                className={cn(
                  "w-full py-2.5 rounded-xl text-sm font-medium transition-colors",
                  "border border-[var(--color-border)]",
                  "hover:bg-[var(--color-surface-secondary)]"
                )}
                style={{ color: 'var(--color-text-secondary)' }}
              >
                Go to Dashboard
              </button>
            ) : (
              <SignInButton mode="modal">
                <button className={cn(
                  "w-full py-2.5 rounded-xl text-sm font-medium transition-colors",
                  "border border-[var(--color-border)]",
                  "hover:bg-[var(--color-surface-secondary)]"
                )} style={{ color: 'var(--color-text-secondary)' }}>
                  Get Started
                </button>
              </SignInButton>
            )}
          </motion.div>

          {/* Pro Plan */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className={cn(
              "relative rounded-2xl p-6 border-2 overflow-hidden"
            )}
            style={{
              backgroundColor: 'var(--color-surface)',
              borderColor: 'var(--color-accent)',
            }}
          >
            {/* Gradient Strip */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-yellow-500 to-orange-500" />

            {/* Badge */}
            <div className="absolute top-4 right-4">
              <span className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 text-white">
                Most Popular
              </span>
            </div>

            <h3 className="text-lg font-semibold text-[var(--color-text)]">Pro</h3>
            <div className="mt-4 mb-6">
              <span className="text-4xl font-bold text-[var(--color-text)]">$5</span>
              <span className="text-sm" style={{ color: 'var(--color-text-muted)' }}> CAD/month</span>
            </div>
            <p className="text-sm mb-3" style={{ color: 'var(--color-text-muted)' }}>
              Everything in Free, plus:
            </p>
            <ul className="space-y-3 mb-8">
              {proFeatures.map((f) => (
                <FeatureRow key={f} label={f} />
              ))}
            </ul>
            <button
              onClick={() => window.location.href = '/checkout'}
              className={cn(
                "w-full py-2.5 rounded-xl text-sm font-medium transition-colors",
                "bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]"
              )}
            >
              Upgrade Now
            </button>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
